import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  ActivityIcon,
  ClipboardCheckIcon,
  CreditCardIcon,
  FileStackIcon,
  GaugeCircleIcon,
  GitPullRequestIcon,
  Globe2Icon,
  KeySquareIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  PackageIcon,
  SettingsIcon,
  UsersIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

const NAV = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboardIcon, exact: true },
  { to: '/app/specs', label: 'Specs', icon: FileStackIcon },
  { to: '/app/audits', label: 'Audits', icon: ClipboardCheckIcon },
  { to: '/app/repos', label: 'SDK repos', icon: PackageIcon },
  { to: '/app/docs-domains', label: 'Docs', icon: Globe2Icon },
  { to: '/app/pr-checks', label: 'PR checks', icon: GitPullRequestIcon },
  { to: '/app/drift', label: 'Drift', icon: ActivityIcon },
  { to: '/app/assets', label: 'Assets', icon: GaugeCircleIcon },
  { to: '/app/billing', label: 'Billing', icon: CreditCardIcon },
  { to: '/app/usage', label: 'Usage', icon: GaugeCircleIcon },
  { to: '/app/settings/org', label: 'Settings', icon: SettingsIcon },
  { to: '/app/settings/members', label: 'Members', icon: UsersIcon },
  { to: '/app/settings/api-tokens', label: 'API tokens', icon: KeySquareIcon },
] as const;

export function AppShell() {
  const { session, signOut } = useAuth();
  const { location } = useRouterState();
  const path = location.pathname;

  return (
    <div className="flex h-full">
      <aside className="flex w-64 flex-col border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
        <div className="flex items-center gap-2 px-6 py-5 font-semibold">
          <span className="inline-block h-6 w-6 rounded-md bg-[var(--color-accent)]" />
          Athena
        </div>
        <nav className="flex-1 space-y-1 px-3">
          {NAV.map((item) => {
            const { to, label, icon: Icon } = item;
            const exact = 'exact' in item ? item.exact : false;
            const active = exact ? path === to : path === to || path.startsWith(`${to}/`);
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm',
                  active
                    ? 'bg-slate-100 font-medium text-slate-900 dark:bg-slate-800 dark:text-slate-100'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-900',
                )}
              >
                <Icon className="h-4 w-4" aria-hidden />
                {label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-slate-200 px-3 py-3 dark:border-slate-800">
          <div className="mb-2 truncate px-3 text-xs text-slate-500">{session?.user.email}</div>
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={signOut}>
            <LogOutIcon className="h-4 w-4" aria-hidden /> Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
